// src/pages/exercise/components/UI/ExerciseTimerPanel.jsx
import { useEffect, useState, useRef, useCallback } from 'react';
import { useStore } from '../Stores/exerciseStore';
import './ExerciseTimerPanel.css';

// ── Popup lines — rotate sequentially, never repeat back-to-back ──────────────
const EARLY_EXIT_LINES = [
  "That was surprisingly quick. Are you sure you did the whole thing?",
  "World record attempt? The timer says you're only halfway there.",
  "Bold move finishing early. Your body might disagree, but respect.",
  "Either you're superhuman, or you need a few more minutes. Just saying.",
  "Half the time, all the credit? We like the enthusiasm.",
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function parseDurationSeconds(durationStr) {
  const match = durationStr?.match(/(\d+)\s*min/i);
  return match ? parseInt(match[1], 10) * 60 : 600;
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

const CIRCLE_RADIUS = 54;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

// ── Component ─────────────────────────────────────────────────────────────────
export function ExerciseTimerPanel() {
  const activeTimer     = useStore((s) => s.activeTimer);
  const stopTimer       = useStore((s) => s.stopTimer);
  const completeMission = useStore((s) => s.completeMission);

  const [totalSeconds, setTotalSeconds]     = useState(0);
  const [secondsLeft, setSecondsLeft]       = useState(0);
  const [isRunning, setIsRunning]           = useState(false);
  const [showEarlyPopup, setShowEarlyPopup] = useState(false);
  const [currentLine, setCurrentLine]       = useState('');
  const [visible, setVisible]               = useState(false);

  // Rotating index — persists across popup triggers within a session
  const lineIndexRef = useRef(0);

  // ── Advance to next line in rotation on each popup open ──────────────────
  const getNextLine = useCallback(() => {
    const line = EARLY_EXIT_LINES[lineIndexRef.current % EARLY_EXIT_LINES.length];
    lineIndexRef.current += 1;
    return line;
  }, []);

  // ── Init / reset when activeTimer changes ────────────────────────────────
  useEffect(() => {
    if (activeTimer) {
      const total = parseDurationSeconds(activeTimer.exercise.duration);
      setTotalSeconds(total);
      setSecondsLeft(total);
      setIsRunning(true);
      setShowEarlyPopup(false);
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [activeTimer]);

  // ── Countdown tick ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isRunning || secondsLeft <= 0) {
      if (secondsLeft <= 0 && totalSeconds > 0) {
        handleConfirmDone();
      }
      return;
    }
    const id = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [isRunning, secondsLeft]);

  const handleConfirmDone = useCallback(() => {
    if (!activeTimer) return;
    completeMission(activeTimer.missionIndex);
    stopTimer();
    setShowEarlyPopup(false);
  }, [activeTimer, completeMission, stopTimer]);

  const handleDoneClick = () => {
    const elapsed = totalSeconds - secondsLeft;
    const halfway = totalSeconds / 2;
    if (elapsed < halfway) {
      setIsRunning(false);
      setCurrentLine(getNextLine());
      setShowEarlyPopup(true);
    } else {
      handleConfirmDone();
    }
  };

  const handleKeepGoing = () => {
    setShowEarlyPopup(false);
    setIsRunning(true);
  };

  if (!activeTimer) return null;

  const exercise = activeTimer.exercise;
  const progress = totalSeconds > 0 ? secondsLeft / totalSeconds : 0;
  const strokeDashoffset = CIRCLE_CIRCUMFERENCE * progress;

  return (
    <>
      {/* ── Timer Panel ── */}
      <div
        className={`etp-root${visible ? ' etp-root--visible' : ''}`}
        onWheel={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="etp-header">
          <p className="etp-label">Now Active</p>
          <h2 className="etp-exercise-name">{exercise.name}</h2>
          <span className="etp-duration-tag">{exercise.duration}</span>
        </div>

        <div className="etp-divider" />

        {/* Circular timer */}
        <div className="etp-clock-wrap">
          <svg className="etp-ring" viewBox="0 0 120 120">
            <circle
              className="etp-ring__track"
              cx="60" cy="60" r={CIRCLE_RADIUS}
            />
            <circle
              className="etp-ring__progress"
              cx="60" cy="60" r={CIRCLE_RADIUS}
              strokeDasharray={CIRCLE_CIRCUMFERENCE}
              strokeDashoffset={strokeDashoffset}
            />
          </svg>
          <div className="etp-clock-center">
            <span className="etp-time">{formatTime(secondsLeft)}</span>
            <span className="etp-time-label">
              {isRunning ? 'remaining' : 'paused'}
            </span>
          </div>
        </div>

        {/* Controls */}
        <div className="etp-controls">
          <button
            className="etp-btn etp-btn--pause"
            onClick={() => setIsRunning((r) => !r)}
          >
            {isRunning ? 'Pause' : 'Continue'}
          </button>
          <button
            className="etp-btn etp-btn--done"
            onClick={handleDoneClick}
          >
            Done
          </button>
        </div>
      </div>

      {/* ── Early Exit Popup ── */}
      {showEarlyPopup && (
        <div className="etp-overlay">
          <div className="etp-popup">
            <p className="etp-popup__eyebrow">Hold on —</p>
            <p className="etp-popup__question">{currentLine}</p>
            <div className="etp-popup__actions">
              <button
                className="etp-popup__btn etp-popup__btn--keep"
                onClick={handleKeepGoing}
              >
                Keep Going
              </button>
              <button
                className="etp-popup__btn etp-popup__btn--confirm"
                onClick={handleConfirmDone}
              >
                I'm Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}